import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAutomations from '../../../hooks/automationhooks/useAutomations';
import FormTemplate from '../../../forms/FormTemplate';
import {
    Autocomplete,
    Button,
    Box,
    Grid,
    TextField,
    Typography
} from '@mui/material';
import SelectArrow from '../../../styles/icons/SelectArrow';
import TriggerModeHeaderControl from './TriggerModeHeaderControl';

export default function CRMSideMenu({ onClose, onSubmit, selectedNode, preSelectedTemplate = null }) {
    const COLOR_SURFACE = '#2C2B2D';
    const COLOR_HEADER = '#1E1E1E';
    const COLOR_ACCENT = '#0DA3E2';
    const { brandId } = useParams();
    const { handleGetCampaigns, handleGetIntegrations, campaigns } = useAutomations();
    const [selectedCampaign, setSelectedCampaign] = useState();
    const [selectedIntegration, setSelectedIntegration] = useState();
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [triggerMode, setTriggerMode] = useState("regular");

    useEffect(() => {
        if (brandId) {
            handleGetCampaigns(brandId);
        }
    }, [brandId]);

    useEffect(() => {
        const existingTriggerMode = selectedNode?.data?.triggerMode;
        if (existingTriggerMode === "api" || existingTriggerMode === "regular") {
            setTriggerMode(existingTriggerMode);
        }
    }, [selectedNode]);

    const fetchIntegrations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await handleGetIntegrations(brandId, selectedCampaign.Id);
            setIntegrations(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [brandId, selectedCampaign]);

    const handleCampaignChange = (event, newValue) => {
        setSelectedCampaign(newValue);
        setSelectedIntegration(null); // reset selection when campaign changes
        setIntegrations([]);
    };

    const handleSubmitLocal = () => {
        if (selectedNode?.data) {
            selectedNode.data.triggerMode = triggerMode;
        }
        const data = {
            id: selectedIntegration.Id,
            name: selectedIntegration.Name,
            triggerMode
        };
        onSubmit(data);
        onClose();
    };

    return (
        <FormTemplate
            title={selectedNode ? 'Edit CRM Node' : 'Create CRM Node'}
            isValid={selectedIntegration}
            onClose={onClose}
            handleSubmit={handleSubmitLocal}
            headerRightAction={
                <TriggerModeHeaderControl value={triggerMode} onChange={setTriggerMode} />
            }
        >
            <Grid container direction="column" spacing={3} sx={{ width: '100%', maxWidth: '854px' }}>
                <Grid item sx={{ width: '100%' }}>
                    <Typography sx={{ color: COLOR_ACCENT, mb: 0.75, fontWeight: 500 }}>
                        Select Campaign
                    </Typography>
                    <Autocomplete
                        options={campaigns.data || []}
                        sx={{ width: '100%' }}
                        value={selectedCampaign}
                        onChange={handleCampaignChange}
                        getOptionLabel={(option) => option?.Name || ''}
                        isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
                        popupIcon={<SelectArrow />}
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 0.5,
                                    bgcolor: COLOR_HEADER,
                                    backgroundImage: 'none',
                                    color: '#FFFFFF',
                                    border: `1px solid ${COLOR_ACCENT}`,
                                    borderRadius: '3px',
                                    '& .MuiAutocomplete-option': {
                                        minHeight: 42,
                                        '&[aria-selected="true"]': {
                                            backgroundColor: 'rgba(13,163,226,0.22)',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(13,163,226,0.12)',
                                        },
                                    },
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: 56,
                                        color: '#FFFFFF',
                                        bgcolor: COLOR_HEADER,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLOR_ACCENT,
                                            borderRadius: '7px',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLOR_ACCENT,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLOR_ACCENT,
                                        },
                                    },
                                    '& .MuiAutocomplete-popupIndicator': {
                                        color: COLOR_ACCENT,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>


                <Grid item sx={{ width: '100%' }}>
                    <Typography sx={{ color: COLOR_ACCENT, mb: 0.75, fontWeight: 500 }}>
                        Select Integration
                    </Typography>
                    {loading ? (
                        <Typography>Loading integrations...</Typography>
                    ) : integrations.length === 0 ? (
                        <Typography color="rgba(255,255,255,0.75)">
                            {selectedCampaign ? 'No Integrations Found' : 'Choose campaign then load integrations'}
                        </Typography>
                    ) : (
                        <Autocomplete
                            options={integrations}
                            value={selectedIntegration}
                            onChange={(event, value) => {
                                setSelectedIntegration(value);
                            }}
                            getOptionLabel={(option) => option.Name || option.Id?.toString() || 'Unnamed'}
                            popupIcon={<SelectArrow />}
                            slotProps={{
                                paper: {
                                    sx: {
                                        mt: 0.5,
                                        bgcolor: COLOR_HEADER,
                                        backgroundImage: 'none',
                                        color: '#FFFFFF',
                                        border: `1px solid ${COLOR_ACCENT}`,
                                        borderRadius: '3px',
                                        '& .MuiAutocomplete-option': {
                                            minHeight: 42,
                                            '&[aria-selected="true"]': {
                                                backgroundColor: 'rgba(13,163,226,0.22)',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: 'rgba(13,163,226,0.12)',
                                            },
                                        },
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label=""
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            height: 56,
                                            color: '#FFFFFF',
                                            bgcolor: COLOR_HEADER,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: COLOR_ACCENT,
                                                borderRadius: '7px',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: COLOR_ACCENT,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: COLOR_ACCENT,
                                            },
                                        },
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: COLOR_ACCENT,
                                        },
                                    }}
                                />
                            )}
                            sx={{ width: '100%' }}
                        />
                    )}
                </Grid>
                <Grid item>
                    <Button
                        onClick={fetchIntegrations}
                        disabled={!selectedCampaign}
                        variant="contained"
                        sx={{
                            width: '229px',
                            minHeight: '50px',
                            borderRadius: '7px',
                            textTransform: 'none',
                            boxShadow: 0,
                            color: '#FFFFFF',
                            px: '12px',
                            py: '10px',
                            backgroundColor: COLOR_ACCENT,
                            '&:hover': {
                                backgroundColor: COLOR_ACCENT,
                                opacity: 0.92,
                            },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(13, 163, 226, 0.45)',
                                color: '#FFFFFF',
                            },
                        }}
                    >
                        <Typography sx={{ fontFamily: 'Assistant, sans-serif', fontWeight: 700, fontSize: '18px', lineHeight: 1 }}>
                            Load Integrations
                        </Typography>
                    </Button>
                </Grid>
            </Grid>
        </FormTemplate>
    );
}
